import { STORAGE_KEYS, SALE_STATUS, SALE_SYNC_STATUS, COLLECTIONS } from "const";
import { db } from "./DBHelper";
import EstimateService from "services/EstimateService";
import DateTimeHelpers from "./DateTimeHelpers";
import * as SalesHelper from "./SalesHelper";
import toast from "react-hot-toast";

export const assignEstimateNumber = async (sale) => {
  if (sale?.estimate_no) {
    return sale;
  }
  const suffix = Number(localStorage.getItem(STORAGE_KEYS.ESTIMATE_BILL_NUMBER_SUFFIX) || 0);

  const updatedSale = {
    ...sale,
    estimate_no: suffix,
  };

  return updatedSale;
};

export const incrementEstimateSuffix = () => {
  const lastUsed = Number(localStorage.getItem(STORAGE_KEYS.ESTIMATE_BILL_NUMBER_SUFFIX) || 0);
  const nextNo = lastUsed + 1;
  localStorage.setItem(STORAGE_KEYS.ESTIMATE_BILL_NUMBER_SUFFIX, String(nextNo));
  return nextNo;
};

export const syncToAPI = async (sale, selectedCustomer) => {
  const terminalId = localStorage.getItem(STORAGE_KEYS.TERMINAL_ID);

  const payload = {
    cess: sale.cess || 0,
    customer_id: selectedCustomer?.id || null,
    estimate_no: sale.estimate_no,
    estimate_no_suffix: sale.estimate_no,
    initiated_terminal_id: Number(terminalId),
    items: (sale.items || []).map((item) => ({
      item_id: item.id,
      quantity: item.qty,
      rate: item.price,
      tax: item.tax || 0,
      cess: item.cess || 0,
      payable: item.payable,
      description: item.name || item.item?.name || "",
    })),
    payable: sale.payable,
    remark: "",
    status: SALE_STATUS.DRAFT,
    tax: sale.tax || 0,
    time: DateTimeHelpers.getLocalISOString(),
    total_amount: sale.total || sale.payable,
    waiter_id: sale.waiter?.id || null,
  };

  try {
    let res;
    if (sale.id) {
      res = await EstimateService.updateEstimate(sale.id, payload);
      toast.success("Estimate updated successfully");
    } else {
      res = await EstimateService.createEstimate(payload);
      toast.success("Estimate created successfully");
    }
    return res;
  } catch (err) {
    console.error("Estimate API error:", err);
    toast.error(err.message || "Failed to sync estimate");
    throw err;
  }
};

export const createEstimate = async ({ activeSale, selectedCustomer, onFinalizationComplete }) => {
  try {
    // 1. Assign number
    const isNewEstimate = !activeSale.estimate_no;
    let updatedSale = await assignEstimateNumber(activeSale);
    updatedSale.status = SALE_STATUS.PAID;
    updatedSale.sync_status = SALE_SYNC_STATUS.PENDING;

    // 2. Save to DB
    await SalesHelper.saveSaleInDB(updatedSale, { notify: true });

    // 3. API Sync
    const res = await syncToAPI(updatedSale, selectedCustomer);
    if (res) {
      // 4. Update sync status on success
      updatedSale.sync_status = SALE_SYNC_STATUS.SYNCED_TO_CLOUD;
      if (res?.data?.id) {
        updatedSale.id = res.data.id;
      }
      await SalesHelper.saveSaleInDB(updatedSale, { notify: true });

      // 5. Increment suffix if it was a new estimate
      if (isNewEstimate) {
        incrementEstimateSuffix();
      }

      // 6. Optionally fetch and update item IDs for conversion
      await updateEstimateItemsWithIds(updatedSale.id || res?.data?.id, updatedSale.local_id);

      onFinalizationComplete?.();
      return { res, updatedSale };
    }
  } catch (err) {
    console.error("Estimate creation failed:", err);
    throw err;
  }
};

function buildEstimatePayload(saleToSync, terminalId) {
  return {
    cess: saleToSync.cess || 0,
    customer_id: saleToSync.customer?.id || null,
    estimate_no: saleToSync.estimate_no,
    estimate_no_suffix: saleToSync.estimate_no,
    initiated_terminal_id: Number(terminalId),
    items: (saleToSync.items || []).map((item) => ({
      item_id: item.id,
      quantity: item.qty,
      rate: item.price || item.rate,
      tax: item.tax || 0,
      cess: item.cess || 0,
      payable: item.payable,
      description: item.name || item.item?.name || "",
    })),
    payable: saleToSync.payable,
    remark: "",
    status: SALE_STATUS.DRAFT,
    tax: saleToSync.tax || 0,
    time: saleToSync.time || DateTimeHelpers.getLocalISOString(),
    total_amount: saleToSync.total || saleToSync.payable,
    waiter_id: saleToSync.salesperson?.id || null,
  };
}

export const syncEstimateOnlyToServer = async (saleToSync) => {
  let finalSyncStatus = SALE_SYNC_STATUS.PENDING;
  let syncMessage = "";

  const terminalId = localStorage.getItem(STORAGE_KEYS.TERMINAL_ID);
  const payload = buildEstimatePayload(saleToSync, terminalId);

  try {
    const res = await EstimateService.createEstimate(payload);
    if (res?.data?.id) {
      finalSyncStatus = SALE_SYNC_STATUS.SYNCED_TO_CLOUD;
      saleToSync.id = res.data.id;
    } else {
      finalSyncStatus = SALE_SYNC_STATUS.FAILED;
    }
  } catch (err) {
    console.error("Estimate sync error:", err);
    finalSyncStatus = SALE_SYNC_STATUS.PENDING;
    syncMessage = err?.message || "Failed to sync estimate";
  }

  const localUpdate = {
    ...saleToSync,
    sync_status: finalSyncStatus,
    sync_message: syncMessage,
  };

  try {
    await SalesHelper.saveSaleInDB(localUpdate, { skipNotify: true });

    if (finalSyncStatus === SALE_SYNC_STATUS.SYNCED_TO_CLOUD && saleToSync.id) {
      await updateEstimateItemsWithIds(saleToSync.id, saleToSync.local_id);
    }

    return finalSyncStatus;
  } catch (error) {
    console.error("Failed to update estimate DB");
    return finalSyncStatus;
  }
};

export const syncPendingEstimates = async () => {
  try {
    const pendingSales = await db.filter(COLLECTIONS.SALES.name, {
      sync_status: [SALE_SYNC_STATUS.PENDING, SALE_SYNC_STATUS.FAILED],
    });

    const pendingEstimates = pendingSales.filter((sale) => sale.estimate_no && !sale.bill_no && !sale.id);

    if (pendingEstimates.length > 0) {
      for (const estimate of pendingEstimates) {
        await syncEstimateOnlyToServer(estimate);
      }
    }
  } catch (err) {
    console.error("Failed to sync pending estimates");
  }
};

export const updateEstimateItemsWithIds = async (serverId, localId) => {
  if (!serverId || !localId) return;

  try {
    const res = await EstimateService.getEstimateDetails(serverId);
    const serverItems = res?.data?.items || [];
    if (serverItems.length === 0) return;

    const localSale = await SalesHelper.getSaleById(localId);
    if (!localSale) return;

    const updatedItems = localSale.items.map((localItem) => {
      const serverItem = serverItems.find(
        (si) => si.item?.id === localItem.id || si.item?.name === (localItem.name || localItem.item?.name),
      );
      if (serverItem) {
        return { ...localItem, server_item_id: serverItem.id };
      }
      return localItem;
    });

    await SalesHelper.saveSaleInDB({ ...localSale, items: updatedItems });
  } catch (error) {
    console.error("Failed to update estimate item IDs:", error);
  }
};
